import { throttle } from 'lodash';
import { InputLabel, makeStyles, TextField } from '@material-ui/core';
import { clsx } from '@components/Provider';
import { CustomizedTypography, Modal } from '@components/Customized';
import {
  CapaErrorCode,
  useFetchPurchaseOrdersQuery,
  useFetchQuotationUserRfqDetailQuery
} from '@generated/graphql';
import { processEvents } from '@utils/customEvent';
import { PaymentModalProps } from './types';
import usePaymentModal from './usePaymentModal';
import { useRef, useState } from 'react';
import useQueryString from '@hooks/useQueryString';

const PaymentApprovalModal: React.FunctionComponent<
  PaymentModalProps & {
    rfqId?: string;
    quotationPartnerId?: string;
  }
> = ({ rfqId = '', quotationPartnerId = '', openButton }) => {
  const [isValid, setIsValid] = useState<boolean>(false);
  const escrowVerifiedPW = useRef<string>('');
  const {
    open,
    onOpen,
    onClose,
    loading,
    setLoading,
    enqueueSnackbar
  } = usePaymentModal();
  const classes = useStyles();
  const { data: rfqData } = useFetchQuotationUserRfqDetailQuery({
    variables: {
      rfqId
    },
    skip: !rfqId
  });

  const { data } = useFetchPurchaseOrdersQuery({
    variables: {
      input: {
        quotationPartnerId,
        rfqId
      }
    },
    skip: !rfqId || !quotationPartnerId,
    fetchPolicy: 'cache-and-network'
  });

  const purchaseOrder = data?.fetchPurchaseOrders?.data?.[0];
  const purchaseOrderId = purchaseOrder?.id ?? '';
  const projectName =
    rfqData?.fetchQuotationUserRfqDetail?.data?.projectName ?? '';
  const partnerOrganizationName =
    purchaseOrder?.receiver?.organizationName ?? '';
  const totalAmount = purchaseOrder?.paymentDataList?.[0].totalAmount ?? 0;
  const { query, onReplaceQueryString } = useQueryString<any>();

  const onSubmit = throttle(async () => {
    setLoading(true);
    try {
      const result = await processEvents.구매확정({
        rfqId,
        quotationPartnerId,
        purchaseOrderId,
        escrowVerifiedPW: escrowVerifiedPW.current
      });
      const isSuccess = result.result === CapaErrorCode.Success;
      if (!isSuccess) throw result;
      onReplaceQueryString({
        ...(query as any),
        p: '7'
      });
      enqueueSnackbar('구매확정이 완료되었습니다.');
      onClose();
    } catch (e) {
      enqueueSnackbar(
        '구매확정에 실패했습니다. 입력한 정보가 정확한지 다시 확인해주세요.'
      );
    } finally {
      setLoading(false);
    }
  }, 500);

  return (
    <>
      {openButton(onOpen)}
      <Modal open={open} onClose={onClose}>
        <Modal.CloseButton onClose={onClose} />
        <Modal.Title>구매확정</Modal.Title>

        <Modal.ContentSection>
          <Modal.Text
            className={classes.text}
            variant="body1"
            color="textPrimary"
          >
            파트너에게 구매확정 하고 거래를 마무리하시겠습니까? 구매확정 후 최소
            3영업일, 최대 5영업일 내에 파트너에게 거래 대금이 지급됩니다.
          </Modal.Text>

          <InputLabel className={classes.escrowPasswordInput}>
            <CustomizedTypography
              variant="body1"
              fontWeight="500"
              gutterBottoms={12}
            >
              사업자등록번호/휴대폰 번호
            </CustomizedTypography>
            <TextField
              variant="outlined"
              fullWidth
              size="medium"
              placeholder="결제시 등록한 사업자등록번호/휴대폰 번호를 입력해주세요"
              onChange={({ currentTarget: input }) => {
                const value = input.value.replace(/[^0-9]/g, '');
                setIsValid(value.length > 0);
                escrowVerifiedPW.current = value;
                input.value = value;
              }}
            />
          </InputLabel>

          <Field title="프로젝트 명" value={projectName} gutterBottom />
          <Field
            title="파트너 명"
            value={partnerOrganizationName}
            gutterBottom
          />
          <Field
            title="구매확정금액"
            value={`${totalAmount.toLocaleString('kr')}원`}
            primary
          />
        </Modal.ContentSection>

        <Modal.ButtonSection>
          <Modal.Button color="default" onClick={onClose} disabled={loading}>
            취소
          </Modal.Button>
          <Modal.Button
            color="primary"
            onClick={onSubmit}
            disabled={loading || !isValid}
          >
            <i className="mb-10 request-gtm-confirm-finish">완료</i>
          </Modal.Button>
        </Modal.ButtonSection>
      </Modal>
    </>
  );
};

interface FieldProps {
  title: string;
  value: string;
  primary?: boolean;
  gutterBottom?: boolean;
}

const Field = ({ title, value, primary, gutterBottom }: FieldProps) => {
  const classes = useStyles();

  return (
    <div
      className={clsx(classes.fieldRoot, {
        gutterBottom
      })}
    >
      <CustomizedTypography
        variant="body2"
        color="textSecondary"
        gutterBottoms={2}
      >
        {title}
      </CustomizedTypography>
      <CustomizedTypography
        variant="subtitle1"
        fontWeight="500"
        color={primary ? 'primary' : 'textPrimary'}
      >
        {value}
      </CustomizedTypography>
    </div>
  );
};

export default PaymentApprovalModal;

const useStyles = makeStyles(
  {
    escrowPasswordInput: {
      margin: '24px 0px'
    },
    text: {
      wordBreak: 'break-all'
    },
    fieldRoot: {
      display: 'flex',
      flexDirection: 'column',
      '&.gutterBottom': {
        marginBottom: 16
      }
    }
  },
  { classNamePrefix: 'payment-approval-modal' }
);
